// ADR 2.10.26q §3 — the Sentry event processor for faults inside the wasm.
//
// A Rust panic on wasm32 aborts into an `unreachable` trap, which reaches
// Sentry as `RuntimeError: unreachable` with no message. `rust-core-wasm`'s
// panic hook keeps `message @ file:line` in a slot; this processor reads it
// through `wasm/telemetrySlots.ts` and files it as `contexts.rust_panic`.
//
// Registered as an integration at `sentry.init` (`lifecycle.ts`), so it runs
// before `beforeSend` — and `redactSentryPayload` therefore redacts the panic
// string, which can format user input, like any other field.
//
// §4's half — a wasm `debug_meta` image and frames rewritten to
// `instruction_addr` so Sentry can symbolicate them — is deferred with §2 by
// §1's measurement: the toolchain cannot yet produce the debug file it would
// point at. The trigger below already recognises wasm frames, so §4 lands here.

import type { Event, StackFrame } from '@sentry/react'

import { readLastPanic } from '../wasm/telemetrySlots'

/** The frame shape a browser gives code inside a wasm module. */
const WASM_FRAME = 'wasm-function['

function isWasmFrame(frame: StackFrame): boolean {
  return [frame.filename, frame.abs_path, frame.function].some((field) => field?.includes(WASM_FRAME) ?? false)
}

/** A trap (`RuntimeError`) or any exception whose stack passes through the wasm. */
export function isWasmFault(event: Event): boolean {
  return (event.exception?.values ?? []).some(
    (value) => value.type === 'RuntimeError' || (value.stacktrace?.frames ?? []).some(isWasmFrame),
  )
}

/**
 * The reader is guarded: an export may not answer after a trap, and a
 * telemetry processor that threw would cost the event it was enriching.
 */
function lastPanic(): string | null {
  try {
    return readLastPanic()
  } catch {
    return null
  }
}

/** Adds `contexts.rust_panic` to a wasm fault that has a recorded panic; anything else passes through. */
export function attachRustPanic<E extends Event>(event: E): E {
  if (!isWasmFault(event)) return event
  const message = lastPanic()
  if (message === null) return event
  return { ...event, contexts: { ...event.contexts, rust_panic: { message } } }
}

/**
 * The integration `lifecycle.ts` hands `sentry.init`, beside the defaults (an
 * array is merged with them, never a replacement). Typed by shape because
 * `@sentry/react` does not re-export `Integration`.
 */
export function wasmFramesIntegration(): { name: string; processEvent: typeof attachRustPanic } {
  return { name: 'WasmFrames', processEvent: attachRustPanic }
}
